import React from "react";

import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { Box, IconButton, Stack, TextField } from "@mui/material";
import { SelectAutocomplete } from "components/forms";
import { Menu } from "components/popups";
import { Notice } from "components/Prompts/Notice";
import { ValidationError } from "components/Validation-error/ValidationError";
import { useIsViewerPermission, usePrompt } from "hooks";
import { useDealActions, useTaskDealsSelect } from "store";
import { DealBase, MeasureParameter } from "store/types";

import { Group as IGroup, useGroupsForm } from "./GroupsFormContext";

interface Props extends IGroup {
  measureParameters: MeasureParameter[];
  selectedMeasure: MeasureParameter | null;
  onIconClick: () => void;
  isExpanded: boolean;

  subDeals: { title: string; id: number }[];

  enableDealSelect: boolean;
  enableExpandArrow: boolean;

  disableDealSelect?: boolean;
  disableSubDealSelect?: boolean;
  disableRemoveGroup?: boolean;

  hideRoundsManage: boolean;

  original_measure_id: number | null;
}

export const GroupHead: React.FC<Props> = ({
  id,
  deal_id,
  sub_deal_id,
  selectedMeasure,
  measureParameters,
  onIconClick,
  isExpanded,
  enableDealSelect,
  enableExpandArrow,
  disableDealSelect,
  disableSubDealSelect,
  disableRemoveGroup,
  hideRoundsManage,
  subDeals
}) => {
  const deals = useTaskDealsSelect();
  const { updateGroup, deleteGroup, manageRounds, getDealRoundsErrors, setImportCommitmentsModal } = useGroupsForm();
  const { downloadCommitmentsTemplateAction } = useDealActions();
  const isAnalystViewerPermission = useIsViewerPermission("file_upload");
  const openPrompt = usePrompt();

  const effectiveDealId = sub_deal_id || deal_id;

  const errors = Boolean(getDealRoundsErrors(effectiveDealId));

  return (
    <Stack direction="row" gap="8px" flexGrow={1}>
      {enableExpandArrow ? (
        <IconButton
          sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
          size="sm"
          onClick={onIconClick}
        >
          <ArrowForwardIosSharpIcon sx={{ transform: isExpanded ? "rotate(270deg)" : "rotate(90deg)" }} />
        </IconButton>
      ) : (
        <Box sx={{ width: 24 }} />
      )}

      <Stack sx={{ flexGrow: 1, gap: "20px" }}>
        {enableDealSelect && (
          <SelectAutocomplete
            fullWidth
            options={deals}
            disabled={!isExpanded || disableDealSelect}
            renderInput={(params) => <TextField {...params} label="Deal" />}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: DealBase) => (
              <li {...props} key={option.id}>
                {option.title}
              </li>
            )}
            value={deals.find((deal) => deal.id === deal_id) || null}
            // setValue={(newValue) => updateGroup({ id, deal_id: newValue?.id })}
            getOptionLabel={(option) => option.title}
            compareParam={(p) => p.title}
          />
        )}
        <Stack direction="row" sx={{ gap: "10px", alignItems: "center" }}>
          {Boolean(subDeals.length) && (
            <SelectAutocomplete
              fullWidth
              options={subDeals}
              disabled={!isExpanded || disableSubDealSelect}
              renderInput={(params) => <TextField {...params} label="Sub Deal" />}
              renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: DealBase) => (
                <li {...props} key={option.id}>
                  {option.title}
                </li>
              )}
              value={deals.find((deal) => deal.id === sub_deal_id) || null}
              setValue={(newValue) => updateGroup({ id, sub_deal_id: newValue?.id || null })}
              getOptionLabel={(option) => option.title}
              compareParam={(p) => p.title}
            />
          )}
          <SelectAutocomplete
            fullWidth
            disableClearable
            options={measureParameters}
            disabled={!isExpanded}
            renderInput={(params) => <TextField {...params} label="Parameter" />}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: MeasureParameter) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
            value={selectedMeasure}
            setValue={(newValue) => updateGroup({ id, measure_id: newValue?.id || null })}
            getOptionLabel={(option) => option.name}
            compareParam={(p) => p.name}
          />
          {!disableRemoveGroup ? (
            <Menu
              iconButtonProps={{ sx: { p: 0, width: 24 } }}
              options={[
                {
                  title: "Delete",
                  icon: <DeleteIcon />,
                  onClick: () => deleteGroup(id)
                },
                {
                  title: "Edit Rounds",
                  icon: <EditOutlinedIcon />,
                  onClick: () => manageRounds(sub_deal_id || deal_id),
                  hidden: hideRoundsManage
                },
                {
                  title: "Download Template",
                  icon: <FileDownloadIcon />,
                  onClick: () => downloadCommitmentsTemplateAction()
                },
                {
                  title: "Import Commitments",
                  icon: <UploadFileRoundedIcon />,
                  onClick: () => {
                    if (isAnalystViewerPermission) {
                      openPrompt({ Component: Notice, props: { type: "trial" } });
                      return;
                    }
                    setImportCommitmentsModal({ deal_id, sub_deal_id });
                  }
                }
              ]}
            />
          ) : (
            <Box width={24} />
          )}
        </Stack>
        {errors && <ValidationError messages={["Rounds check failed, please check rounds"]} />}
      </Stack>
    </Stack>
  );
};
